import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ACCESS_TOKEN_KEY } from "@/lib/constants";
import { getVerificationPrompt, markVerificationPromptSeen } from "@/lib/services";

/** Where the pop-up would get in the way: signing in or up, onboarding, and verifying itself. */
const QUIET_PATHS = [
  "/login", "/register", "/signup-success", "/activate-account", "/verify-email",
  "/forgot-password", "/reactivate-account", "/onboarding",
  "/employment-verification", "/employment-verify",
];

const isQuiet = (pathname: string) => QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Radix returns focus to a Dialog.Trigger on close, and this dialog opens by
 * itself, so focus would fall to <body>. Start again at the page's content, as
 * a new page does (ScrollManager).
 */
function focusPageContent(event: Event) {
  event.preventDefault();
  const main = document.querySelector("main");
  if (!main) return;
  if (!main.hasAttribute("tabindex")) {
    main.setAttribute("tabindex", "-1");
    main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
  }
  main.focus({ preventScroll: true });
}

const hasToken = () => {
  try {
    return Boolean(localStorage.getItem(ACCESS_TOKEN_KEY));
  } catch {
    return false;
  }
};

/**
 * Tells members who haven't verified that company verification works now, once:
 * the first time they're in the app after the rebuild (on their next login, or
 * their next visit if they stayed signed in). The server decides who it's for
 * and remembers that it was shown, across devices.
 */
export default function VerificationPrompt() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const checkedFor = useRef<string | null>(null);
  const quiet = isQuiet(pathname);
  const quietRef = useRef(quiet);
  quietRef.current = quiet;
  const member = user?.username ?? null;

  useEffect(() => {
    if (!member || quiet || checkedFor.current === member || !hasToken()) return;
    checkedFor.current = member;
    getVerificationPrompt()
      .then(({ show }) => {
        if (!show) return;
        if (quietRef.current) {
          // They moved on to a sign-in or verification page meanwhile: ask again on the next page.
          checkedFor.current = null;
          return;
        }
        setOpen(true);
        // Once means once: recorded as soon as it appears, whatever they click.
        markVerificationPromptSeen().catch(() => {});
      })
      .catch(() => {
        checkedFor.current = null;
      });
    // pathname: after a failed check, the next page asks again.
  }, [member, quiet, pathname]);

  function verifyNow() {
    setOpen(false);
    navigate("/employment-verification");
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto" onCloseAutoFocus={focusPageContent}>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[#CA8A04]/15">
            <BadgeCheck className="h-6 w-6 text-[#A16207]" aria-hidden />
          </div>
          <DialogTitle className="text-center text-xl">Company verification now works</DialogTitle>
          <DialogDescription className="text-center text-base text-gray-600">
            We've made it simpler. Enter your work email, choose your company, and type in the
            6-digit code we send you. It takes about a minute, and then you can post, comment and review your company as a verified employee.
          </DialogDescription>
        </DialogHeader>
        <p className="text-center text-sm text-gray-500">
          Use your company email. Gmail, Yahoo and other personal addresses won't work.
        </p>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            onClick={verifyNow}
            className="h-11 w-full bg-[#CA8A04] font-semibold text-white hover:bg-[#B8860B]"
          >
            Verify my company
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 w-full text-gray-600">
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
